import { getSupabaseClient } from "../../shared/supabase";
import type { Database } from "../../shared/types/database.generated";

export type ChangeLog = Database["public"]["Tables"]["change_log"]["Row"];
export type EditorIdentity = { display_name: string; editor_colour: string };
export type ChangeLogWithAuthor = ChangeLog & { author?: EditorIdentity };
export type SearchResult = Database["public"]["Functions"]["search_authoring"]["Returns"][number];

function client() {
  const result = getSupabaseClient();
  if (!result.ok) throw new Error(result.message);
  return result.client;
}

export async function searchAuthoring(query: string, offset = 0, includeArchived = false) {
  const parameters = {
    p_query: query,
    p_limit: 30,
    p_offset: offset,
    p_include_archived: includeArchived,
  };
  const [legacy, core] = await Promise.all([
    client().rpc("search_authoring", parameters),
    (client() as any).rpc("search_core_authoring", parameters),
  ]);
  if (legacy.error) throw legacy.error;
  if (core.error) throw core.error;
  const retired = new Set(["knight_definition", "building_definition", "knight_card_definition", "equipment_definition", "resource_definition", "feature_definition", "quest_definition", "live_event"]);
  return [...(core.data ?? []), ...(legacy.data ?? []).filter((row) => !retired.has(row.entity_type))]
    .slice(0, 30) as SearchResult[];
}

export async function listHistory(entityType: string, entityId: string) {
  const { data, error } = await client()
    .from("change_log")
    .select("*")
    .eq("table_name", entityType)
    .eq("row_id", entityId)
    .order("created_at", { ascending: false })
    .limit(100);
  if (error) throw error;
  const identities = await loadIdentities(data.map((row) => row.user_id));
  return data.map((row) => ({ ...row, author: identities.get(row.user_id) })) satisfies ChangeLogWithAuthor[];
}

export async function listRecentHistory(limit = 100) {
  const { data, error } = await client().from("change_log").select("*").order("created_at", { ascending: false }).limit(limit);
  if (error) throw error;
  const identities = await loadIdentities(data.map((row) => row.user_id));
  return data.map((row) => ({ ...row, author: identities.get(row.user_id) })) satisfies ChangeLogWithAuthor[];
}

export async function undoChange(changeId: string) {
  const { data, error } = await client().rpc("safe_undo_change", { p_change_id: changeId });
  if (error) throw error;
  return data;
}

export function subscribeToGlobalActivity(refresh: () => void) {
  const channel = client().channel("global-authoring-activity")
    .on("postgres_changes", { event: "INSERT", schema: "public", table: "change_log" }, refresh)
    .subscribe();
  return () => { void client().removeChannel(channel); };
}

async function loadIdentities(ids: string[]) {
  const unique = [...new Set(ids)];
  if (unique.length === 0) return new Map<string, EditorIdentity>();
  const { data, error } = await client().from("profiles").select("id,display_name,editor_colour").in("id", unique);
  if (error) throw error;
  return new Map(data.map((profile) => [profile.id, { display_name: profile.display_name, editor_colour: profile.editor_colour }]));
}

export function subscribeToEntityActivity(entityType: string, entityId: string, refresh: () => void) {
  const channel = client()
    .channel(`entity-activity:${entityType}:${entityId}`)
    .on("postgres_changes", { event: "INSERT", schema: "public", table: "change_log", filter: `row_id=eq.${entityId}` }, refresh)
    .subscribe();
  return () => { void client().removeChannel(channel); };
}
